/**
 * Helpers for Facebook click tracking (fbc / fbp).
 *
 * The Facebook pixel normally stores the click id from the `fbclid` URL
 * param in the `_fbc` cookie. When the pixel is blocked (ad blockers,
 * tracking protection, delayed consent), that cookie never gets set even
 * though the fbclid is present in the landing URL. These helpers use the
 * pixel's cookie as the primary source and reconstruct the value from the
 * URL in Meta's documented format `fb.1.<timestamp>.<fbclid>` as a backup.
 * https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
 */

const FBC_COOKIE = '_fbc';
const FBP_COOKIE = '_fbp';
const NINETY_DAYS_SECONDS = 90 * 24 * 60 * 60;

export const getCookieByName = name => {
  if (typeof document === 'undefined') {
    return null;
  }
  const allCookies = document.cookie.split(';');
  for (let i = 0; i < allCookies.length; i++) {
    const cookiePair = allCookies[i].trim().split('=');
    if (decodeURIComponent(cookiePair[0]) === name) {
      return decodeURIComponent(cookiePair[1]);
    }
  }
  return null;
};

const getFbclidFromUrl = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return new URLSearchParams(window.location.search).get('fbclid');
};

const buildFbcFromFbclid = fbclid => `fb.1.${Date.now()}.${fbclid}`;

/**
 * Facebook click id in fbc format.
 * Primary: `_fbc` cookie set by the pixel. Backup: built from the `fbclid`
 * URL param of the current page.
 */
export const getFbc = () => {
  const cookieValue = getCookieByName(FBC_COOKIE);
  if (cookieValue) {
    return cookieValue;
  }
  const fbclid = getFbclidFromUrl();
  return fbclid ? buildFbcFromFbclid(fbclid) : null;
};

/** Facebook browser id from the `_fbp` cookie set by the pixel. */
export const getFbp = () => getCookieByName(FBP_COOKIE);

/**
 * Call once on app load. If the user landed with an fbclid but the pixel
 * hasn't stored it in the `_fbc` cookie (e.g. the pixel is blocked), persist
 * it ourselves so signup/checkout can still read it after SPA navigation has
 * dropped the query param.
 */
export const captureFbclidToCookie = () => {
  if (typeof document === 'undefined') {
    return;
  }
  const fbclid = getFbclidFromUrl();
  if (!fbclid) {
    return;
  }
  const existing = getCookieByName(FBC_COOKIE);
  // The pixel already tracks this exact click; keep its cookie (and original timestamp).
  if (existing && existing.endsWith(`.${fbclid}`)) {
    return;
  }
  const value = encodeURIComponent(buildFbcFromFbclid(fbclid));
  document.cookie = `${FBC_COOKIE}=${value}; max-age=${NINETY_DAYS_SECONDS}; path=/; SameSite=Lax`;
};
